import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Plane, MapPin, Trophy, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FlightTickets = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [collectedCount, setCollectedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [worldTourComplete, setWorldTourComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gamification/flight-tickets`, { withCredentials: true });
      setRoutes(response.data.routes);
      setCollectedCount(response.data.collected_count);
      setTotalCount(response.data.total_count);
      setWorldTourComplete(response.data.world_tour_complete);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalDistance = routes.reduce((sum, r) => r.collected ? sum + r.distance : sum, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-500 to-blue-600 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                <Plane className="w-8 h-8 text-sky-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Uçuş Rotalarım</h1>
                <p className="text-sky-100 text-sm">Dünya turunu tamamla!</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{collectedCount}/{totalCount}</p>
              <p className="text-sky-200 text-xs">Rota Açıldı</p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="mt-4 bg-white/20 rounded-full h-3">
            <div 
              className="bg-white h-3 rounded-full transition-all"
              style={{ width: `${(collectedCount / totalCount) * 100}%` }}
            />
          </div>
          <p className="text-sky-100 text-xs mt-2 text-center">
            {totalDistance.toLocaleString()} km uçtun! ✈️
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
        {/* World Tour Badge */}
        {worldTourComplete && (
          <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-400 to-yellow-500 mb-6">
            <CardContent className="p-4 text-center text-white">
              <Trophy className="w-12 h-12 mx-auto mb-2" />
              <h3 className="text-xl font-bold">🌍 Dünya Turu Tamamlandı!</h3>
              <p className="text-amber-100 text-sm">Tüm rotaları keşfettin!</p>
            </CardContent>
          </Card>
        )}

        {/* Routes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {routes.map((route) => (
            <Card 
              key={route.id}
              className={`border-0 shadow-lg transition-all ${
                route.collected 
                  ? 'bg-gradient-to-r from-sky-50 to-blue-50 ring-2 ring-sky-300' 
                  : 'bg-white opacity-60'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* From */}
                    <div className="text-center">
                      <span className="text-2xl">{route.flag_from}</span>
                      <p className="text-xs font-medium text-gray-600 mt-1">{route.from}</p>
                    </div>
                    
                    {/* Arrow with plane */}
                    <div className="flex items-center gap-1 px-2">
                      <div className="h-px w-6 bg-gray-300"></div>
                      <Plane className={`w-5 h-5 ${route.collected ? 'text-sky-500' : 'text-gray-300'}`} />
                      <div className="h-px w-6 bg-gray-300"></div>
                    </div>
                    
                    {/* To */}
                    <div className="text-center">
                      <span className="text-2xl">{route.flag_to}</span>
                      <p className="text-xs font-medium text-gray-600 mt-1">{route.to}</p>
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div className="text-right">
                    {route.collected ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <Check className="w-5 h-5" />
                        <span className="text-xs font-bold">Toplandı</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-400">
                        <MapPin className="w-5 h-5" />
                        <span className="text-xs">Kilitli</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{route.distance.toLocaleString()} km</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info */}
        <Card className="border-0 shadow-lg mt-6 bg-gradient-to-r from-sky-50 to-blue-50">
          <CardContent className="p-4">
            <h3 className="font-bold text-gray-800 mb-2">📍 Nasıl Rota Açarım?</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Her tamamlanan modül = 1 yeni rota</li>
              <li>• Final sınavını geçince bonus rota</li>
              <li>• 10 rota = "Dünya Turu" rozeti!</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FlightTickets;
